import { memo, useCallback } from "react";
import { getHandlingTimeLabel } from "@repo/api-sdk";
import {
  freeShippingLabel,
  FreeShippingPrompt
} from "@/components/cart/FreeShippingPrompt";
import { QuantityControl } from "@/components/ui/QuantityControl";
import {
  useFreeShippingMeter,
  useRemoveCartItem,
  useUpdateCartItem
} from "@/queries/cart";
import { formatPrice } from "@/utils/money";
import { Tooltip } from "@medusajs/ui";
import { TruncateTooltip } from "@repo/design-system";
import { cn } from "@repo/design-system/utils";
import { switchTargetFor } from "@/utils/supplierComparison";
import type {
  ComparisonColumn,
  ComparisonProductRow
} from "@/utils/supplierComparison";
import { SupplierComparisonCell } from "./SupplierComparisonCell";

export interface SupplierComparisonGridTableProps {
  rows: ComparisonProductRow[];
  columns: ComparisonColumn[];
  /** Per-seller-id subtotal for only the products currently bought from that supplier; `null` if none of the listed products are. */
  subtotals: Record<string, number | null>;
  /** Called when the buyer clicks an eligible cell to switch that row's product to `column`. */
  onSwitchSupplier?: (
    row: ComparisonProductRow,
    column: ComparisonColumn
  ) => void;
  /** `rowKey` of the row currently mid-switch, if any. */
  pendingRowKey?: string | null;
}

/**
 * Shared geometry for the pinned product column (position/bg differ per row
 * type). On a phone it is pinned at a fixed 9rem - a product column sized to
 * its content took ~70% of a 390px viewport and left the supplier columns a
 * sliver - and only opens up to its content width from `sm`.
 */
const STICKY_COL_BASE =
  "sticky left-0 w-36 min-w-36 max-w-36 px-2 py-3 text-left align-top border-r border-gray-100 sm:w-auto sm:min-w-[200px] sm:max-w-none sm:px-4";

/**
 * A sticky header cell can't rely on its own `border-b` painting reliably
 * under `border-collapse`, so the divider is drawn with a box-shadow instead.
 */
const HEADER_CELL =
  "sticky top-0 bg-gray-50 px-3 py-2 text-left font-medium text-gray-700 shadow-[0_1px_0_0_#e5e7eb]";

/** Padding shared by every supplier-column cell, kept tight so more columns fit beside the pinned product column. */
const SUPPLIER_CELL_PADDING = "px-2";

/**
 * Narrowest a supplier column goes, on a phone and a desktop alike. Wider only
 * spreads suppliers apart and pushes the next one off screen. At 98px a price
 * cell's box (the cell's own 8px side padding, then the cell's 8px inner
 * padding) is about 66px wide, enough for an ordinary price ("$29.95 each"); a
 * four-figure price line is the first thing to run out of room. The
 * free-shipping note wraps to fit.
 */
const SUPPLIER_COLUMN_MIN_WIDTH = "min-w-[98px]";

/**
 * The footer's free-shipping note box, amber and green alike: centred under
 * the price, capped at 112px so the copy wraps to 3-4 lines however wide the
 * column stretches (a note as wide as the column read as a bar), and padded
 * tight to keep the footer short.
 */
const SHIPPING_NOTE_BOX = "mx-auto mt-1 max-w-[112px] px-1 py-0.5";

/** Width cap for a supplier name and its handling time, so both stay inside the narrower column. */
const SUPPLIER_NAME_WIDTH = "max-w-[84px]";

/**
 * Reserves room for a typical subtotal state (price + a free-shipping note
 * wrapped to 3 lines) in every footer cell. Subtotals count only what the
 * buyer currently takes from each supplier, so a single switch changes two
 * columns at once - letting those cells size to their content would resize the
 * footer, and with it the table and everything below it, on every click.
 *
 * The note box is capped at 112px, so it usually wraps to 3 lines. Reserving
 * for the worst case (4-5 lines) left a visible gap under every shorter note;
 * the occasional longer note grows the row to fit rather than clipping.
 */
const SUBTOTAL_BODY = "min-h-[5rem]";

/**
 * The comparison grid itself: products as rows, suppliers as columns. Kept
 * separate from the data-fetching panel so it's plain props in, JSX out.
 *
 * A real `<table>` (there's no shared Table component in this repo yet) with
 * a horizontally-scrolling wrapper and a sticky first column, so it works on
 * a phone without hiding the tab - only the supplier columns scroll.
 */
export const SupplierComparisonGridTable = memo(
  function SupplierComparisonGridTable({
    rows,
    columns,
    subtotals,
    onSwitchSupplier,
    pendingRowKey
  }: SupplierComparisonGridTableProps) {
    // The wrapper is its own bounded scroll viewport. The header row can only
    // freeze relative to a scroll container, and an `overflow-x` wrapper
    // already is one - so bounding the height here is what lets `sticky top-0`
    // below actually pin, and keeps the pinned first column consistent.
    //
    // From `md` the page lays itself out as a column the height of the
    // viewport (CartPage), so this just shrinks to whatever is left and the
    // page never scrolls. A phone has no such column, so it keeps a fixed cap.
    // The 12rem floor is for a very short window: below it the grid stops
    // shrinking and the page scrolls instead, rather than the grid collapsing
    // to a sliver with nothing to scroll the page.
    //
    // `isolate` keeps the pinned header's z-20 inside this box. Without it the
    // header competes with the page's own sticky band (z-10) and, once the page
    // has scrolled the grid up under that band, draws over it.
    return (
      <div className="isolate max-h-[calc(100vh-15rem)] overflow-auto rounded-lg border border-gray-200 bg-white md:max-h-none md:min-h-[12rem]">
        <table className="w-full min-w-max border-collapse text-sm">
          <thead>
            <tr>
              <th
                scope="col"
                // z-30: sits above both the sticky header row and the sticky
                // first column, since it belongs to both.
                className={cn(
                  STICKY_COL_BASE,
                  HEADER_CELL,
                  "z-30 px-2 sm:px-4"
                )}
              >
                Product
              </th>
              {columns.map((column) => (
                <th
                  key={column.sellerId}
                  scope="col"
                  className={cn(
                    HEADER_CELL,
                    // Overrides HEADER_CELL's `text-left` (shared with the
                    // sticky "Product" header, which should stay left-aligned)
                    // - supplier names read better centered above a column of
                    // centered prices. `align-top` keeps a wrapped 2-line name
                    // starting at the same baseline as a short 1-line one.
                    "z-20 border-l border-gray-100 text-center align-top",
                    SUPPLIER_COLUMN_MIN_WIDTH,
                    SUPPLIER_CELL_PADDING
                  )}
                >
                  {/* The block is exactly 2 lines tall (2 * 1.25rem) whether
                      the name needs them or not, so the handling time below
                      starts at the same row for every column - a short name
                      just leaves blank space instead of pulling the ETA up
                      underneath it, and a long one is clamped with an
                      ellipsis. `text-sm leading-5` pins the line height:
                      the th sets none, so it would otherwise inherit the
                      page's and the 2.5rem would not be 2 lines. */}
                  <TruncateTooltip
                    as="div"
                    lines={2}
                    className={cn(
                      "mx-auto h-10 text-sm leading-5",
                      SUPPLIER_NAME_WIDTH
                    )}
                  >
                    {column.sellerName}
                  </TruncateTooltip>
                  {/* Handling time sits here rather than in every cell -
                      it's the same for all of a supplier's offers in the
                      normal case, so repeating it down the column is noise.
                      When it genuinely varies by product the column has no
                      single value and the cells show it instead. */}
                  {column.handlingTime && (
                    <div
                      className={cn(
                        "mx-auto mt-0.5 text-xs font-normal text-gray-500",
                        SUPPLIER_NAME_WIDTH
                      )}
                    >
                      {getHandlingTimeLabel(column.handlingTime)}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => {
              const isRowPending = pendingRowKey === row.rowKey;
              return (
                <tr
                  key={row.rowKey}
                  className="border-b border-gray-100 last:border-b-0"
                >
                  <SupplierComparisonRowHeader row={row} />
                  {columns.map((column) => (
                    <td
                      key={column.sellerId}
                      className={cn(
                        "border-l border-gray-100 py-3 align-top",
                        SUPPLIER_CELL_PADDING
                      )}
                    >
                      <SupplierComparisonCell
                        cell={row.cells[column.sellerId]}
                        isCurrentSupplier={
                          row.currentSellerId === column.sellerId
                        }
                        isCheapest={row.cheapestSellerId === column.sellerId}
                        // Only when the column header can't state it once for
                        // the whole supplier (i.e. their offers disagree).
                        showHandlingTime={column.handlingTime === null}
                        isPending={isRowPending}
                        // Clickable only when the pair is actually switchable -
                        // `switchTargetFor` is the one owner of that rule, so
                        // the cell's affordance and the handler's guard can't
                        // drift apart.
                        onClick={
                          onSwitchSupplier && switchTargetFor(row, column)
                            ? () => onSwitchSupplier(row, column)
                            : undefined
                        }
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr className="border-t-2 border-gray-200 bg-gray-50">
              <th
                scope="row"
                className={cn(
                  STICKY_COL_BASE,
                  "z-10 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500"
                )}
              >
                Subtotal
              </th>
              {columns.map((column) => (
                <SupplierSubtotalCell
                  key={column.sellerId}
                  column={column}
                  subtotal={subtotals[column.sellerId] ?? null}
                />
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }
);

/**
 * What "Save $X" is measured against, for its tooltip. The figure is not a
 * supplier discount: it is how much less the buyer pays than at the dearest
 * supplier that can actually sell the product (`activePriceExtremes`), times
 * the quantity on this line.
 */
function savingExplanation(quantity: number): string {
  return `How much less you pay than at the most expensive supplier for this product, for your quantity of ${quantity}. Suppliers who are out of stock or inactive aren't counted.`;
}

/**
 * The pinned first column's content for one product row: title, cost per
 * item (left) with a live quantity stepper (right), and the saving line.
 * Its own component (rather than inline JSX in the `rows.map` above) because
 * editing quantity needs its own mutation hooks bound to this row's current
 * cart - hooks can't be called conditionally inside a `.map` callback.
 */
const SupplierComparisonRowHeader = memo(function SupplierComparisonRowHeader({
  row
}: {
  row: ComparisonProductRow;
}) {
  const cartId = row.currentCartId ?? "";
  // Destructured rather than held as whole mutation objects: useMutation
  // rebuilds those every render, so callbacks keyed on them would change
  // identity each render too (same reason `useShippingMethodSync` does this).
  const { setQuantity } = useUpdateCartItem(cartId);
  const { mutateAsync: removeItem, isPending: isRemoving } =
    useRemoveCartItem(cartId);
  // Only a row with a real current pick has anything to update/remove - the
  // "current-unpriced" fallback rows and any row with no pick at all keep the
  // old plain "Qty N" text instead of a control with nothing to act on.
  const canEditQuantity =
    row.currentCartId !== null && row.currentLineItemId !== null;
  // Quantity edits are debounced and patched into the cache on the click, so
  // only a removal locks the row - see `CartLineItem`.
  const isPending = isRemoving;

  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      if (!row.currentLineItemId) return;
      setQuantity(row.currentLineItemId, newQuantity);
    },
    [row.currentLineItemId, setQuantity]
  );

  const handleRemove = useCallback(async () => {
    if (!row.currentLineItemId || isPending) return;
    await removeItem(row.currentLineItemId);
  }, [row.currentLineItemId, removeItem, isPending]);

  return (
    <th scope="row" className={cn(STICKY_COL_BASE, "z-10 bg-white")}>
      <TruncateTooltip
        as="div"
        lines={3}
        className="font-medium text-gray-900 sm:max-w-[220px]"
      >
        {row.productTitle}
      </TruncateTooltip>

      {/* Variant directly under the name, as in the cart list and order
          summary. Absent for products with no real options, so single-variant
          rows don't gain a blank line - unlike the saving line below, this
          one is stable for a given row, so collapsing it can't re-flow the
          table mid-interaction. */}
      {row.variantTitle && (
        <TruncateTooltip
          as="div"
          className="mt-0.5 text-xs text-gray-500 sm:max-w-[220px]"
        >
          {row.variantTitle}
        </TruncateTooltip>
      )}

      <div
        className={cn(
          // Price above the stepper on a phone: side by side they need more
          // than the pinned column's 9rem.
          "mt-1 flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2",
          isPending && "pointer-events-none opacity-60"
        )}
      >
        <span className="text-xs text-gray-500">
          {row.currentUnitPrice !== null
            ? `${formatPrice(row.currentUnitPrice)} each`
            : null}
        </span>
        {canEditQuantity ? (
          <QuantityControl
            quantity={row.quantity}
            onQuantityChange={handleQuantityChange}
            onRemove={handleRemove}
            size="sm"
            min={1}
          />
        ) : (
          <span className="text-xs text-gray-500">Qty {row.quantity}</span>
        )}
      </div>

      {/* Always rendered at a fixed height, empty or not: the saving appears
          and disappears as the buyer switches supplier or quantity, and
          letting it collapse would re-flow every row below it on each
          change. */}
      <div className="mt-1 h-4 text-xs font-medium text-green-700">
        {row.savingFormatted ? (
          // `@medusajs/ui`'s tooltip, the one mounted app-wide (see
          // `LiveProvenanceBadge`). `tabIndex` because Radix's `asChild`
          // trigger doesn't make a `span` focusable, and without it the
          // explanation is hover-only.
          <Tooltip content={savingExplanation(row.quantity)} side="top">
            <span
              tabIndex={0}
              className="cursor-help rounded underline decoration-dotted underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-1"
            >
              Save {row.savingFormatted}
            </span>
          </Tooltip>
        ) : null}
      </div>
    </th>
  );
});

/**
 * One footer cell: the supplier's subtotal for only the products the
 * customer is currently buying from them (the purple-ringed cells in that
 * column, not everything they merely carry). A supplier shown purely for
 * comparison (none of the listed products are currently bought from them)
 * gets "-" rather than a hypothetical total.
 *
 * Under the subtotal sits that supplier's free-shipping note, but only when
 * the supplier already has an active seller-cart, since the threshold needs a
 * real `cart_id` to look up (see `useFreeShippingMeter`). It stays in its
 * supplier's column, directly under the figure it is about; a line listed
 * beneath the table instead has to repeat the supplier's name and reads as
 * detached from the grid.
 */
const SupplierSubtotalCell = memo(function SupplierSubtotalCell({
  column,
  subtotal
}: {
  column: ComparisonColumn;
  subtotal: number | null;
}) {
  const hasCart = column.cartId !== null;

  const {
    freeShippingThreshold,
    amountToFreeShipping,
    freeShippingSavings,
    freeShippingOptionName
  } = useFreeShippingMeter({
    cartId: column.cartId ?? "",
    itemSubtotal: subtotal ?? 0,
    selectedShippingOptionId: column.selectedShippingOptionId,
    enabled: hasCart && subtotal !== null
  });

  const cellClassName = cn(
    "border-l border-gray-100 pb-1.5 pt-2 align-top",
    SUPPLIER_CELL_PADDING
  );

  if (subtotal === null) {
    return (
      <td className={cellClassName}>
        <div className={cn(SUBTOTAL_BODY, "px-2 text-gray-400")}>-</div>
      </td>
    );
  }

  const thresholdMet =
    hasCart && !!freeShippingThreshold && !amountToFreeShipping;

  return (
    <td className={cellClassName}>
      <div className={SUBTOTAL_BODY}>
        <div className="px-2 font-semibold text-gray-900">
          {formatPrice(subtotal)}
        </div>
        {hasCart && amountToFreeShipping ? (
          // Boxed by SHIPPING_NOTE_BOX so it wraps within the column instead
          // of forcing the whole column wider to fit on one line.
          // `shippingSavings` passed through so this reads the same copy as
          // the by-supplier accordion and the order-summary rail, rather than
          // the shorter fallback wording. `announce={false}` for the same
          // reason the accordion mutes: the order-summary rail carries this
          // cart's meter on every centre-column tab and owns the live region.
          <FreeShippingPrompt
            amountToFreeShipping={amountToFreeShipping}
            freeShippingThreshold={freeShippingThreshold}
            shippingSavings={freeShippingSavings}
            shippingOptionName={freeShippingOptionName}
            announce={false}
            className={SHIPPING_NOTE_BOX}
          />
        ) : thresholdMet ? (
          // Same box shape as `FreeShippingPrompt` above (rounded rectangle,
          // bordered, same padding/text size) rather than the pill-shaped
          // `Badge` - the two states are the same message ("you're covered
          // on shipping") at different distances from the threshold, so they
          // should read as the same kind of note, just green instead of amber.
          // Qualified by the same option name as the amber copy: the threshold
          // zeroes one service, so a buyer on Express must not read this as
          // "your freight is covered".
          <div
            className={cn(
              SHIPPING_NOTE_BOX,
              "rounded border border-green-200 bg-green-50 text-xs font-normal text-green-600"
            )}
          >
            Free {freeShippingLabel(freeShippingOptionName)}
          </div>
        ) : null}
      </div>
    </td>
  );
});
